// services/InvestmentValidationService.ts

import { GlobalConfiguration } from '../models/GlobalConfiguration';
import { globalConfigurationCache } from 'GlobalConfigurationCache';
import { validateInvestment } from './InvestmentValidator';

/**
 * Service that provides investment validation using GlobalConfiguration values
 */
class InvestmentValidationService {
  private cachedConfig: GlobalConfiguration | null = null;

  /**
   * Get the current global configuration
   */
  private async getConfig(): Promise<GlobalConfiguration> {
    if (this.cachedConfig) {
      return this.cachedConfig;
    }

    const config = await globalConfigurationCache.getConfig();
    this.cachedConfig = config;
    return config;
  }

  /**
   * Clear the cached configuration to force refresh
   */
  clearCache(): void {
    this.cachedConfig = null;
  }

  /**
   * Validates that a given numeric investment meets the requirements from GlobalConfiguration
   * - is at least the minimum investment amount
   * - is a multiple of the investment increment
   */
  async validateInvestment(value?: string | number): Promise<{
    validInvestment: number | undefined;
    validationError: string | undefined;
  }> {
    try {
      const config = await this.getConfig();
      return validateInvestment(value, config);
    } catch (error) {
      console.error('Failed to load global configuration for validation:', error);
      return validateInvestment(value);
    }
  }

  /**
   * Get the current minimum investment and increment values
   */
  async getInvestmentParameters(): Promise<{
    minimumInvestment: number;
    investmentIncrement: number;
  }> {
    try {
      const config = await this.getConfig();
      return {
        minimumInvestment: config?.minimumInvestment ?? 100000,
        investmentIncrement: config?.investmentIncrement ?? 25000,
      };
    } catch (error) {
      console.error('Failed to load global configuration for investment parameters:', error);
      return {
        minimumInvestment: 100000,
        investmentIncrement: 25000,
      };
    }
  }
}

// Export a singleton instance
const investmentValidationService = new InvestmentValidationService();
export { investmentValidationService };
export default InvestmentValidationService;
